import React, { useState, useEffect, useRef } from "react";
import "./loginNew.css";
import fusoLogo from "../assets/fuso-logo.svg";
import microsoft from "../assets/microsoft.svg";
// import botright from "../assets/Robot_desktop.svg";
import botright from "../assets/Robot_desktop 1.svg";
// import botMobile from "../assets/Robot_mobile.svg";
import botMobile from "../assets/Robot_desktop 1.svg";
import ascentchatLogin from "../assets/ascentchatLogin.svg";
import shop from "../assets/shopmanuallogin.svg";
import pnc from "../assets/pncLogin.svg";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { handleLoginSuccess, generateSecureRandomString, generateSecureUserId } from "../index.js";
import { useTranslation } from "react-i18next";
import usFlag from "../assets/united-states.png";
import jpFlag from "../assets/japan.png";

// Define the base URL based on the environment for PNC endpoints
// Using relative URLs to leverage the proxy configuration
const PNC_BASE_URL = "/v1/fuso-ascent-pnc-search/";

// Define the base URL based on the environment for Shop Manual endpoints.
// Using relative URLs to leverage the proxy configuration
const SHOP_MANUAL_BASE_URL = "/api/v1/shop-manual/";

// Define the PNC API endpoints
const PNC_API_ENDPOINTS = {
  CREATE_USER: `${PNC_BASE_URL}user_details`,
};

// Define the Shop Manual API endpoints
const SHOP_MANUAL_API_ENDPOINTS = {
  CREATE_USER: `${SHOP_MANUAL_BASE_URL}user_details`,
};

const FusoLoginPage = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedModule, setSelectedModule] = useState("Ascent");
  const [loginError, setLoginError] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem("selectedLanguage") || "jp"
  );
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const languageMenuRef = useRef(null);

  const { t, i18n } = useTranslation();

  const languageOptions = [
    { code: "en", name: "English", flag: usFlag, alt: "English" },
    { code: "jp", name: "日本語", flag: jpFlag, alt: "Japanese" },
  ];

  const handleLanguageChange = (langCode) => {
    setSelectedLanguage(langCode);
    i18n.changeLanguage(langCode);
    localStorage.setItem("selectedLanguage", langCode);
    setShowLanguageMenu(false);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setShowLanguageMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navigate = useNavigate();

  // Function to register user with the PNC backend service only
  const registerUserWithPNCBackend = async (email, userId) => {
    try {
      // Check if the URL is properly configured
      if (!PNC_API_ENDPOINTS.CREATE_USER) {
        throw new Error(
          "PNC_API_ENDPOINTS.CREATE_USER is not properly configured"
        );
      }

      const pncResponse = await axios.post(
        PNC_API_ENDPOINTS.CREATE_USER,
        {
          user_email: email,
          user_id: userId, // This should be the raw user ID string without any modification
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 15000, // Increase timeout to 15 seconds for potentially slow connections
        }
      );

      return {
        pncResponse: pncResponse.data,
        status: "success",
      };
    } catch (error) {
      console.error("Error registering user with PNC backend:", error);

      // Handle 409 Conflict (user already exists) specifically
      if (error.response && error.response.status === 409) {
        return {
          pncResponse: { message: "User already exists" },
          status: "already_exists", // Special status to indicate user already exists
        };
      }

      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
        console.error("Response headers:", error.response.headers);
      } else if (error.request) {
        console.error("No response received:", error.request);
      } else {
        console.error("Request setup error:", error.message);
      }
      throw error;
    }
  };

  // Function to register user with the Shop Manual backend service only
  const registerUserWithShopManualBackend = async (email, userId) => {
    try {
      // Check if the URL is properly configured
      if (!SHOP_MANUAL_API_ENDPOINTS.CREATE_USER) {
        throw new Error(
          "SHOP_MANUAL_API_ENDPOINTS.CREATE_USER is not properly configured"
        );
      }

      const shopManualResponse = await axios.post(
        SHOP_MANUAL_API_ENDPOINTS.CREATE_USER,
        {
          user_email: email,
          user_id: userId, // This should be the raw user ID string without any modification
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 15000, // Increase timeout to 15 seconds for potentially slow connections
        }
      );

      return {
        shopManualResponse: shopManualResponse.data,
        status: "success",
      };
    } catch (error) {
      console.error("Error registering user with Shop Manual backend:", error);

      // Handle 409 Conflict (user already exists) specifically
      if (error.response && error.response.status === 409) {
        return {
          shopManualResponse: { message: "User already exists" },
          status: "already_exists", // Special status to indicate user already exists
        };
      }

      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
        console.error("Response headers:", error.response.headers);
      } else if (error.request) {
        console.error("No response received:", error.request);
      } else {
        console.error("Request setup error:", error.message);
      }
      throw error;
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const state = urlParams.get("state");
    const error = urlParams.get("error");
    // Check if user is already fully authenticated and force show modal
    const username =
      localStorage.getItem("username") || sessionStorage.getItem("username");
    const email =
      localStorage.getItem("email") || sessionStorage.getItem("email");
    const userId =
      localStorage.getItem("user_id") || sessionStorage.getItem("user_id");
    const loginStatus =
      localStorage.getItem("login") || sessionStorage.getItem("login");

    if (loginStatus === "true" && username && email && userId) {
      setIsAuthorized(true);
      setShowModal(true);
      setIsLoading(false);
      return;
    }

    if (error) {
      setLoginError("Authentication error: " + error);
      setIsLoading(false);
    } else if (
      code &&
      state &&
      !localStorage.getItem("authCompleted") &&
      !sessionStorage.getItem("authCompleted")
    ) {
      // Mark authentication as completed to prevent loops - store in both localStorage and sessionStorage
      localStorage.setItem("authCompleted", "true");
      sessionStorage.setItem("authCompleted", "true");

      // Redirect to callback handler
      window.location.href = `/oauth2/callback?code=${code}&state=${state}`;
    } else if (
      localStorage.getItem("authCompleted") ||
      sessionStorage.getItem("authCompleted")
    ) {

      // Check if user info is stored
      if (username && email) {
        // If we have user info but don't have a user ID yet, generate one
        if (!userId) {
          // Generate a secure user ID using the email
          const newUserId = generateSecureUserId(email);

          localStorage.setItem("user_id", newUserId);
          localStorage.setItem("login", "true"); // Mark as fully logged in
          localStorage.setItem("authToken", "oauth-token-" + generateSecureRandomString(32));
          sessionStorage.setItem("user_id", newUserId);
          sessionStorage.setItem("login", "true");

          setIsAuthorized(true);
          setShowModal(true);
          setIsLoading(false);
        } else {
          localStorage.setItem("login", "true"); // Ensure login flag is set
          localStorage.setItem("authToken", "oauth-token-" + generateSecureRandomString(32));
          sessionStorage.setItem("login", "true");
          setIsAuthorized(true);
          setShowModal(true);
          setIsLoading(false);
        }
      } else {
        setIsLoading(true);

        const fetchUserInfo = (retryCount = 0, maxRetries = 3) => {
          axios
            .get("/oauth2/userinfo", {
              timeout: 10000,
            })
            .then((response) => {
              const email =
                response.data.preferredUsername || response.data.email;
              const username =
                response.data.preferredUsername ||
                response.data.user ||
                response.data.name;
              const userId = response.data.user || response.data.email;

              if (!username || !email) {
                throw new Error(
                  "Missing username or email in user info response"
                );
              }

              // Store the user information in both localStorage and sessionStorage
              localStorage.setItem("username", username);
              localStorage.setItem("email", email);
              sessionStorage.setItem("username", username);
              sessionStorage.setItem("email", email);

              // Store the OAuth user ID which we'll use as our unique identifier
              if (userId) {
                localStorage.setItem("oauth_user_id", userId);
                sessionStorage.setItem("oauth_user_id", userId);
              }

              // Generate a fallback user ID if needed
              const newUserId = userId || generateSecureUserId(email);

              localStorage.setItem("user_id", newUserId);
              localStorage.setItem("login", "true");
              localStorage.setItem("authToken", "oauth-token-" + generateSecureRandomString(32));
              sessionStorage.setItem("user_id", newUserId);
              sessionStorage.setItem("login", "true");

              setIsAuthorized(true);
              setShowModal(true);
              setIsLoading(false);
            })
            .catch((error) => {
              console.error(`Error in auth flow:`, error);

              if (retryCount < maxRetries) {
                setTimeout(
                  () => fetchUserInfo(retryCount + 1, maxRetries),
                  2000
                );
              } else {
                setLoginError(
                  "Authentication completed but couldn't retrieve your account details. Please try again or contact support."
                );
                // Clear auth completed flags from both storages after max retries
                localStorage.removeItem("authCompleted");
                sessionStorage.removeItem("authCompleted");
                setIsLoading(false);
              }
            });
        };

        fetchUserInfo();
      }
    } else {
      // No authentication in progress
      setIsLoading(false);
    }
  }, []);

  const handleLoginClick = () => {
    // Clear previous login data from both localStorage and sessionStorage
    localStorage.removeItem("login");
    localStorage.removeItem("username");
    localStorage.removeItem("email");
    localStorage.removeItem("user_id");
    localStorage.removeItem("oauth_user_id");
    localStorage.removeItem("authCompleted");

    sessionStorage.removeItem("login");
    sessionStorage.removeItem("username");
    sessionStorage.removeItem("email");
    sessionStorage.removeItem("user_id");
    sessionStorage.removeItem("oauth_user_id");
    sessionStorage.removeItem("authCompleted");

    setLoginError(null);

    // Redirect to OAuth start exactly as in reference code
    window.location.href = "/oauth2/start?rd=/login";
  };

  // Function to register with the appropriate backend based on module type
  const registerWithBackend = async (moduleType) => {
    setIsLoading(true);
    
    const email = localStorage.getItem("email") || sessionStorage.getItem("email");
    const userId = localStorage.getItem("user_id") || sessionStorage.getItem("user_id");
    
    if (!email || !userId) {
      setLoginError("Missing user information. Please try logging in again.");
      setIsLoading(false);
      return false;
    }
    
    try {
      let registrationResult = false;
      
      if (moduleType === "PNC") {
        // Register with PNC backend
        const result = await registerUserWithPNCBackend(email, userId);
        registrationResult = result.status === "success" || result.status === "already_exists";
        // console.log(`PNC registration ${registrationResult ? 'successful' : 'failed'}`);
      } else if (moduleType === "Shop Manual") {
        // Register with Shop Manual backend
        const result = await registerUserWithShopManualBackend(email, userId);
        registrationResult = result.status === "success" || result.status === "already_exists";
        // console.log(`Shop Manual registration ${registrationResult ? 'successful' : 'failed'}`);
      } else if (moduleType === "Ascent") {
        // Ascent doesn't need registration with a backend
        registrationResult = true;
      }
      
      setIsLoading(false);
      return registrationResult;
    } catch (error) {
      console.error(`Error registering with ${moduleType} backend:`, error);
      setLoginError(`Failed to register with ${moduleType}. Please try again.`);
      setIsLoading(false);
      return false;
    }
  };

  const handleButtonClick = async (moduleType) => {
  const registrationSuccessful = await registerWithBackend(moduleType);
  
  if (registrationSuccessful) {
    // Call the enhanced login success handler to set all security markers
    handleLoginSuccess({
      module: moduleType,
      timestamp: Date.now()
    });
    
    // Set navigation security flags to signal this is authorized navigation
    sessionStorage.setItem('navigation_in_progress', 'true');
    sessionStorage.setItem('navigation_timestamp', Date.now().toString());
    sessionStorage.setItem('legitimate_navigation', 'true');
    
    if (moduleType === "Ascent") {
      setSelectedModule("Ascent");
      sessionStorage.setItem('navigation_target', '/ascent');
      navigate("/ascent");
    } else if (moduleType === "Shop Manual") {
      setSelectedModule("Shop Manual");
      sessionStorage.setItem('navigation_target', '/shop-manual');
      navigate("/shop-manual");
    } else {
      setSelectedModule("PNC");
      sessionStorage.setItem('navigation_target', '/pnc');
      navigate("/pnc");
    }
    
    // Clean up navigation flags after a short delay
    setTimeout(() => {
      sessionStorage.removeItem('navigation_in_progress');
      sessionStorage.removeItem('legitimate_navigation');
    }, 500);
  }
};

  // Function to toggle modal - will only open if authorized
  const toggleModal = () => {
    if (isAuthorized) {
      setShowModal(!showModal);
    } else {
      // If not authorized, don't show modal
      setShowModal(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-white overflow-hidden fullPageMob relative">
      {/* Loading Indicator */}
      {isLoading && (
        <div className="fixed inset-0 bg-white bg-opacity-70 z-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
        </div>
      )}

      {/* Mobile View */}
      <div className="flex flex-col md:hidden w-full">
        {/* FUSO Logo at the top for mobile only */}
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 md:hidden">
          <img src={fusoLogo} alt="FUSO Logo" className="h-[24px]" />
        </div>

        {/* Robot Image for Mobile */}
        <div className="w-full flex items-center justify-center bg-white topSection mt-12">
          <div className="w-full robotMobPnc">
            <img
              src={selectedLanguage === "en" ? "/images/english_landing_page.png" : botMobile}
              alt="bot"
              className="w-full h-auto px-4 robotImgTop"
            />
          </div>
        </div>

        {/* Login Section for Mobile */}
        <div className="flex flex-col justify-center items-center w-full p-4 bg-white botLoginPnc">
          <div className="max-w-[36rem] w-full p-4 rounded-lg shadow-lg leftLoginPnc">
            {/* Language Dropdown - Mobile */}
            <div className="flex justify-end mb-3">
              <div className="relative" ref={languageMenuRef}>
                <button
                  className="flex items-center px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer text-sm"
                  onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                  style={{ backgroundColor: showLanguageMenu ? "#FFE0E0" : "white" }}
                >
                  <img
                    src={languageOptions.find((l) => l.code === selectedLanguage)?.flag}
                    alt={languageOptions.find((l) => l.code === selectedLanguage)?.alt}
                    className="w-5 h-4 mr-2 object-cover rounded-sm"
                  />
                  <span className="font-medium mr-1">{selectedLanguage.toUpperCase()}</span>
                  <svg className={`w-4 h-4 transition-transform ${showLanguageMenu ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showLanguageMenu && (
                  <div className="absolute top-full mt-1 right-0 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[130px] z-[100]">
                    {languageOptions.map((lang) => (
                      <button
                        key={lang.code}
                        className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center border-none cursor-pointer text-sm ${selectedLanguage === lang.code ? "bg-gray-50" : ""}`}
                        onClick={() => handleLanguageChange(lang.code)}
                      >
                        <img src={lang.flag} alt={lang.alt} className="w-5 h-4 mr-2 object-cover rounded-sm" />
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="text-center mb-6">
              <h1 className="text-xl mb-2 loginHeadingPnc">
                {t("loginHeading").split("FUSO").map((part, i, arr) => (
                  i < arr.length - 1 ? (
                    <React.Fragment key={i}>{part}<span className="text-red-600">FUSO</span></React.Fragment>
                  ) : part
                ))}
              </h1>
              <p className="text-sm subpartLeftPnc">
                {t("loginSubtext")}
              </p>
            </div>

            {loginError && (
              <div className="text-red-600 text-sm mb-4 text-center">
                {loginError}
              </div>
            )}

            <button
              className="flex items-center justify-center w-full mx-auto bg-gray-900 text-white py-2 px-3 rounded-full mt-4 hover:bg-gray-800 transition microSoftPnc text-sm"
              onClick={handleLoginClick}
              disabled={isLoading}
            >
              <span className="mr-2">
                <img src={microsoft} alt="microsoft" className="h-4" />
              </span>
              {t("loginButton")}
            </button>

            {/* Development Bypass Button */}
            {process.env.NODE_ENV === "development" && (
              <button
                className="flex items-center justify-center w-full mx-auto bg-red-600 text-white py-2 px-3 rounded-full mt-4 hover:bg-red-700 transition text-sm"
                onClick={() => {
                  const mockEmail = process.env.REACT_APP_DEV_EMAIL ;
                  const mockUsername = process.env.REACT_APP_DEV_USERNAME;
                  const mockUserId = generateSecureUserId(mockEmail);

                  localStorage.setItem("username", mockUsername);
                  localStorage.setItem("email", mockEmail);
                  localStorage.setItem("user_id", mockUserId);
                  localStorage.setItem("login", "true");
                  localStorage.setItem("authToken", "oauth-token-" + generateSecureRandomString(32));
                  localStorage.setItem("authCompleted", "true");

                  sessionStorage.setItem("username", mockUsername);
                  sessionStorage.setItem("email", mockEmail);
                  sessionStorage.setItem("user_id", mockUserId);
                  sessionStorage.setItem("login", "true");
                  sessionStorage.setItem("authCompleted", "true");

                  setIsAuthorized(true);
                  setShowModal(true);
                }}
                disabled={isLoading}
              >
                Bypass Login (Dev Only)
              </button>
            )}

          </div>
        </div>
      </div>

      {/* Desktop View - Login at left and Robot at right */}
      <div className="hidden md:flex md:flex-row w-full min-h-screen">
        {/* Login Section for Desktop */}
        <div className="flex flex-col justify-center items-center w-1/2 p-12 bg-white leftLoginMarginPnc">
          <div className="max-w-[36rem] w-full p-8 rounded-lg shadow-lg leftLoginPnc">
            {/* Logo row with language selector */}
            <div className="flex justify-between items-center mb-[24px]">
              <div className="w-8"></div>
              <img src={fusoLogo} alt="FUSO Logo" className="h-[24px]" />
              <div className="relative" ref={languageMenuRef}>
                <button
                  className="flex items-center px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer text-sm"
                  onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                  style={{ backgroundColor: showLanguageMenu ? "#FFE0E0" : "white" }}
                >
                  <img
                    src={languageOptions.find((l) => l.code === selectedLanguage)?.flag}
                    alt={languageOptions.find((l) => l.code === selectedLanguage)?.alt}
                    className="w-5 h-4 mr-2 object-cover rounded-sm"
                  />
                  <span className="font-medium mr-1">{selectedLanguage.toUpperCase()}</span>
                  <svg className={`w-4 h-4 transition-transform ${showLanguageMenu ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showLanguageMenu && (
                  <div className="absolute top-full mt-1 right-0 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[130px] z-[100]">
                    {languageOptions.map((lang) => (
                      <button
                        key={lang.code}
                        className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center border-none cursor-pointer text-sm ${selectedLanguage === lang.code ? "bg-gray-50" : ""}`}
                        onClick={() => handleLanguageChange(lang.code)}
                      >
                        <img src={lang.flag} alt={lang.alt} className="w-5 h-4 mr-2 object-cover rounded-sm" />
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="text-center mb-6">
              <h1 className="text-2xl mb-2 loginHeadingPnc">
                {t("loginHeading").split("FUSO").map((part, i, arr) => (
                  i < arr.length - 1 ? (
                    <React.Fragment key={i}>{part}<span className="text-red-600">FUSO</span></React.Fragment>
                  ) : part
                ))}
              </h1>
              <p className="subpartLeftPnc">
                {t("loginSubtext")}
              </p>
            </div>

            {loginError && (
              <div className="text-red-600 text-sm mb-4 text-center">
                {loginError}
              </div>
            )}

            <button
              className="flex items-center justify-center w-3/4 mx-auto bg-gray-900 text-white py-2 px-3 rounded-full mt-6 hover:bg-gray-800 transition microSoftPnc text-sm"
              onClick={handleLoginClick}
              disabled={isLoading}
            >
              <span className="mr-2">
                <img src={microsoft} alt="microsoft" className="h-4" />
              </span>
              {t("loginButton")}
            </button>

            {/* Development Bypass Button */}
            {process.env.NODE_ENV === "development" && (
              <button
                className="flex items-center justify-center w-3/4 mx-auto bg-red-600 text-white py-2 px-3 rounded-full mt-4 hover:bg-red-700 transition text-sm"
                onClick={() => {
                  const mockEmail = process.env.REACT_APP_DEV_EMAIL;
                  const mockUsername = process.env.REACT_APP_DEV_USERNAME;
                  const mockUserId = generateSecureUserId(mockEmail);

                  localStorage.setItem("username", mockUsername);
                  localStorage.setItem("email", mockEmail);
                  localStorage.setItem("user_id", mockUserId);
                  localStorage.setItem("login", "true");
                  localStorage.setItem("authToken", "oauth-token-" + generateSecureRandomString(32));
                  localStorage.setItem("authCompleted", "true");

                  sessionStorage.setItem("username", mockUsername);
                  sessionStorage.setItem("email", mockEmail);
                  sessionStorage.setItem("user_id", mockUserId);
                  sessionStorage.setItem("login", "true");
                  sessionStorage.setItem("authCompleted", "true");

                  setIsAuthorized(true);
                  setShowModal(true);
                }}
                disabled={isLoading}
              >
                Bypass Login (Dev Only)
              </button>
            )}

          </div>
        </div>

        {/* Robot Image for Desktop */}
        <div className="w-1/2 p-0 flex items-center justify-center rightSectionLogin">
          <div>
            <img
              src={selectedLanguage === "en" ? "/images/english_landing_page.png" : botright}
              alt="bot"
            />
          </div>
        </div>
      </div>

      {/* Modal Component - Different styling for mobile and desktop */}
      {showModal && (
        <div className="fixed inset-0 flex items-end md:items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={toggleModal}
          ></div>
          <div
            className={`bg-white rounded-t-2xl md:rounded-lg p-4 md:p-8 w-full md:max-w-[40rem] relative z-10 modalLogin transition-transform duration-300 ease-in-out ${
              showModal ? "translate-y-0" : "translate-y-full md:translate-y-0"
            }`}
            style={{
              maxHeight: "85vh",
              overflow: "auto",
            }}
          >
            <div className="md:hidden w-full flex justify-center mb-2">
              <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
            </div>

            <div className="text-center mb-4 md:mb-6">
              <h2 className="text-lg md:text-2xl mb-2 loginRedirectPnc">
                {t("modalHeading")}
              </h2>
            </div>

            {/* Mobile view - arranged like in the screenshot */}
            <div className="md:hidden flex flex-col space-y-4">
              <div className="flex flex-row justify-center space-x-4">
                {/* Ascent Chatbot */}
                {/* <div
                  onClick={() => handleButtonClick("Ascent")}
                  className="flex-1 rounded-lg p-4 flex flex-col items-center hover:shadow-md transition cursor-pointer borderCardPnc"
                >
                  <div className="w-12 h-12 flex items-center justify-center mb-2">
                    <img className="h-8" src={ascentchatLogin} alt="ascent" />
                  </div>
                  <span className="text-center text-sm font-medium">
                    Ascent Chatbot
                  </span>
                </div> */}

                {/* Shop Manual */}
                <div
                  onClick={() => handleButtonClick("Shop Manual")}
                  className="flex-1 rounded-lg p-4 flex flex-col items-center hover:shadow-md transition cursor-pointer borderCardPnc"
                >
                  <div className="w-12 h-12 flex items-center justify-center mb-2">
                    <img className="h-8" src={shop} alt="shop manual" />
                  </div>
                  <span className="text-center text-sm font-medium">
                    {t("Shop Manual")}
                  </span>
                </div>
              </div>

              <div className="flex justify-center">
                {/* PNC Search */}
                <div
                  onClick={() => handleButtonClick("PNC")}
                  className="flex-1 rounded-lg p-4 flex flex-col items-center hover:shadow-md transition cursor-pointer borderCardPnc"
                >
                  <div className="w-12 h-12 flex items-center justify-center mb-2">
                    <img className="h-8" src={pnc} alt="pnc" />
                  </div>
                  <span className="text-center text-sm font-medium">
                    {t("PNC")}
                  </span>
                </div>
              </div>
            </div>

            {/* Desktop view - original 3-column grid */}
            <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Shop Manual */}
              <div
                onClick={() => handleButtonClick("Shop Manual")}
                className="border-gray-200 rounded-lg p-4 md:p-6 flex flex-col items-center hover:shadow-md transition cursor-pointer borderCardPnc"
              >
                <div className="w-10 md:w-12 h-[32px] md:h-[40px] rounded-full flex items-center justify-center mb-2">
                  <img
                    className="h-[28px] md:h-[34px]"
                    src={shop}
                    alt="shop manual"
                  />
                </div>
                <span className="text-center text-sm md:text-base font-medium">
                  {t("Shop Manual")}
                </span>
              </div>

              {/* PNC Search */}
              <div
                onClick={() => handleButtonClick("PNC")}
                className="border-gray-200 rounded-lg p-4 md:p-6 flex flex-col items-center hover:shadow-md transition cursor-pointer borderCardPnc"
              >
                <div className="w-10 md:w-12 h-[32px] md:h-[40px] rounded-full flex items-center justify-center mb-2">
                  <img className="h-[28px] md:h-[34px]" src={pnc} alt="pnc" />
                </div>
                <span className="text-center text-sm md:text-base font-medium">
                  {t("PNC")}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FusoLoginPage;